/**
 * Lobby tab partitioning: the home game list splits into a default "Current" tab
 * (everything still in play — lobby, active, pending-invite) and a "Complete"
 * tab holding EVERY finished game, most-recently-completed first.
 *
 * Pure + RN-free so it's unit-testable.
 */
import type { GameResponse } from '@warcarp/api-types'
import { isMyTurn } from './turnHelpers'

/**
 * True if the game has finished. ALL completed games live in the "Complete"
 * tab regardless of how long ago they ended; everything else (active, lobby,
 * pending-invite) stays on the default "Current" tab.
 */
export function isCompleteGame(game: GameResponse): boolean {
  return game.status === 'COMPLETE'
}

/**
 * Sort comparator for the "Complete" tab: most-recently-completed first.
 * Games with a missing or unparseable `completedAt` sort to the bottom.
 */
export function byCompletedAtDesc(a: GameResponse, b: GameResponse): number {
  const parse = (g: GameResponse): number => {
    if (!g.completedAt) return Number.NEGATIVE_INFINITY
    const t = Date.parse(g.completedAt)
    return Number.isNaN(t) ? Number.NEGATIVE_INFINITY : t
  }
  return parse(b) - parse(a)
}

/**
 * The game's "recent activity" timestamp (ms) for lobby ordering.
 *
 * - A game the viewer has been INVITED to (pending invite, not yet joined) has
 *   no gameplay activity of its own, so it keys off the *invite date* — the
 *   `sentAt` of the viewer's own pending invitation. This lets a fresh invite
 *   surface above games whose last turn was longer ago.
 * - A started game keys off `turnStartedAt` — when the current turn began.
 *   Never `updatedAt`: background writes (e.g. the stale-turn reminder email
 *   stamping `staleTurnNotifiedAt`) bump it without any turn being taken,
 *   which reshuffled the list whenever a reminder went out.
 * - A lobby game the viewer has joined keys off `updatedAt` (join, bot added,
 *   setup edit).
 *
 * Returns 0 when nothing parses, sinking the game to the bottom of its tier.
 */
function activityTime(g: GameResponse, viewerUserId: string | undefined): number {
  const raw =
    g.viewerInvitationStatus === 'pending'
      ? (g.invitations?.find((i) => i.user.id === viewerUserId && i.status === 'pending')
          ?.sentAt ?? g.updatedAt)
      : g.status === 'LOBBY'
        ? g.updatedAt
        : (g.turnStartedAt ?? g.startedAt ?? g.updatedAt)
  const t = Date.parse(raw ?? '')
  return Number.isNaN(t) ? 0 : t
}

/** True if the game is pinned to the top tier of the "Current" list: either the
 *  viewer has a pending invite, or it's the viewer's turn. */
function isPinned(g: GameResponse, viewerUserId: string | undefined): boolean {
  return g.viewerInvitationStatus === 'pending' || isMyTurn(g, viewerUserId)
}

/**
 * Sort comparator for the home "Current" tab.
 *
 * Ordering:
 *   1. A PINNED top tier — games the viewer is invited to (pending invite) OR
 *      where it's the viewer's turn. Both are the most time-sensitive, so they
 *      sit above everything else, sorted among themselves by most-recent
 *      activity (invite date for invites, recent turn for your-turn games).
 *   2. Everything else, most-recent activity first (latest turn start desc).
 *
 * See `activityTime` for how each game's "activity" timestamp is derived.
 */
export function compareCurrentTab(
  a: GameResponse,
  b: GameResponse,
  viewerUserId: string | undefined,
): number {
  const aPin = isPinned(a, viewerUserId) ? 0 : 1
  const bPin = isPinned(b, viewerUserId) ? 0 : 1
  if (aPin !== bPin) return aPin - bPin
  return activityTime(b, viewerUserId) - activityTime(a, viewerUserId)
}
